/**
 * Where an answer came from, and how much of it the answer actually used.
 *
 * Rendered as the "Sources:" chip row of Martí's design: one chip per citation with
 * the document and page. The excerpt and the reranker score are not dropped — they
 * live in the chip's tooltip, because a low score on a cited passage is still a
 * signal worth acting on.
 *
 * WHY THERE ARE TWO GROUPS. What this used to present as «fuentes» was everything
 * retrieval returned, not what the answer rests on. Measured over the campaign
 * answers that do answer and carry citations: the model referenced a median of one
 * passage out of the twelve shown at the default level, 85 % leaned on a single
 * document while 2.5 were offered as sources, and 91 % of everything shown was
 * never referenced. Someone checking an answer against four documents when it came
 * from one is being sent to look in the wrong places.
 *
 * Splitting rather than hiding is deliberate, and it cuts both ways. Overstating
 * support is the defect above. Understating it is the opposite defect, already
 * measured in this project: the excerpt shown is narrower than what the model
 * received — each passage reaches it widened with its neighbours — and judging an
 * answer against the narrow excerpt produced a false fabrication finding on 15 of
 * 15 correct answers. So «también consultado» is folded, not dropped: one click,
 * and the count is in the label so folding it never hides how much there is.
 *
 * An answer with no sources at all keeps its warning chip: the failure that
 * destroys confidence in a corporate assistant is a confident answer with nothing
 * behind it, and the interface should not let that look ordinary.
 */

import type { Citation } from "../api/types";
import "./provenance.css";

function Chip({ citation, position }: { citation: Citation; position: number }) {
  return (
    <span
      className={`prov-chip${citation.cited ? " prov-chip--cited" : ""}`}
      title={`[${position}] ${citation.document_name}${
        citation.page_from ? ` · p.${citation.page_from}` : ""
      } · relevancia ${citation.score.toFixed(2)}\n«${citation.excerpt}»`}
    >
      <span className="prov-doc">{citation.document_name}</span>
      {citation.page_from !== null && <span className="prov-page">p.{citation.page_from}</span>}
    </span>
  );
}

export function Provenance({ citations }: { citations: Citation[] }) {
  if (citations.length === 0) {
    return (
      <aside className="prov" aria-label="Sin fuentes">
        <span className="prov-label">Fuentes:</span>
        <span
          className="prov-chip prov-chip--none"
          title="Esta respuesta no cita ningún documento, así que no se puede verificar. Compruébala antes de usarla."
        >
          Sin fuentes — verificar antes de usar
        </span>
      </aside>
    );
  }

  // Positions are 1-based and follow delivery order, which is the same order the
  // model saw them in and the same order the "[n]" markers refer to.
  const numbered = citations.map((citation, index) => ({ citation, position: index + 1 }));
  const cited = numbered.filter((entry) => entry.citation.cited);

  // No marker anywhere means the model did not say which it used, not that it used
  // none. One undivided list is the honest rendering of "cannot tell".
  if (cited.length === 0) {
    return (
      <aside className="prov" aria-label="Fuentes de la respuesta">
        <span className="prov-label">Fuentes:</span>
        {numbered.map((entry) => (
          <Chip
            key={`${entry.citation.document_id}-${entry.citation.rank}`}
            citation={entry.citation}
            position={entry.position}
          />
        ))}
      </aside>
    );
  }

  const consulted = numbered.filter((entry) => !entry.citation.cited);
  return (
    <aside className="prov" aria-label="Fuentes de la respuesta">
      <span className="prov-label">Citado:</span>
      {cited.map((entry) => (
        <Chip
          key={`${entry.citation.document_id}-${entry.citation.rank}`}
          citation={entry.citation}
          position={entry.position}
        />
      ))}
      {consulted.length > 0 && (
        // Plegado, y con el recuento en la etiqueta. Medido sobre las 129
        // respuestas de la campaña que responden y citan: el modelo marcaba una
        // mediana de UN fragmento de los doce mostrados, así que este grupo se
        // llevaba casi toda la fila y lo citado —que es lo que hay que leer—
        // competía con ello. El recuento va en el texto para que plegarlo no
        // esconda cuánto hay detrás.
        //
        // `<details>` nativo y no un estado de React: la plataforma da el
        // teclado, el rol y la semántica de abierto/cerrado, y ninguno de los
        // tres puede desincronizarse de la CSS. Mismo motivo que el `<dialog>`
        // de `ConfirmDialog`.
        <details className="prov-more">
          <summary
            className="prov-label prov-label--muted"
            title="Recuperado por la búsqueda y entregado al modelo, pero la respuesta no lo referencia. Se muestra porque el modelo no siempre marca todo lo que usa."
          >
            También consultado ({consulted.length})
          </summary>
          <div className="prov-more-body">
            {consulted.map((entry) => (
              <Chip
                key={`${entry.citation.document_id}-${entry.citation.rank}`}
                citation={entry.citation}
                position={entry.position}
              />
            ))}
          </div>
        </details>
      )}
    </aside>
  );
}
